/** The frame editor: filmstrip on top, the frame's fields stacked below,
 * and the always-visible footer with Save draft plus the render button:
 * "Create first frame" makes the still AND the scene's video in one go;
 * afterwards the primary button redoes the video and "New first frame"
 * redoes the still (and, with it, the video).
 *
 * Swiping the filmstrip (or tapping a cell) changes the route; the app
 * shell updates this screen's "position" attribute in place, so the
 * strip keeps its scroll state.
 */

import { router } from "../control/router";
import { store } from "../store";
import { ApiError } from "../data/client";
import { frameCount } from "../data/definitions";
import { t, tn } from "../lib/i18n";

import "../components/scapescene-filmstrip";
import "../components/scapescene-frame-fields";
import "../components/scapescene-footer-bar";

type FieldsElement = HTMLElement & { flush?: () => Promise<void> };

export class WorkshopEditorScreen extends HTMLElement {
  static observedAttributes = ["position"];

  #built = false;
  #strip!: HTMLElement;
  #fields!: FieldsElement;
  #footer!: HTMLElement;
  #banner!: HTMLElement;
  #offStore: (() => void) | null = null;
  #rendering = false;

  get position(): number {
    return Math.min(
      frameCount(store.definitions),
      Math.max(1, parseInt(this.getAttribute("position") ?? "1", 10) || 1),
    );
  }

  connectedCallback(): void {
    if (!this.#built) this.#build();
    store.startPolling();
    this.#offStore = store.onChange(() => {
      // A deep link past the last frame is clamped here once the
      // definitions (and so the real frame count) have arrived.
      this.#syncPositionAttrs();
      this.#syncFooter();
    });
    this.#syncFooter();
  }

  disconnectedCallback(): void {
    this.#offStore?.();
    this.#offStore = null;
    void this.#fields?.flush?.();
  }

  attributeChangedCallback(name: string, old: string | null): void {
    if (!this.#built || name !== "position" || old === null) return;
    this.#syncPositionAttrs();
    this.#banner.hidden = true;
    this.#syncFooter();
  }

  #syncPositionAttrs(): void {
    const position = String(this.position);
    if (this.#strip.getAttribute("position") !== position) {
      this.#strip.setAttribute("position", position);
    }
    if (this.#fields.getAttribute("position") !== position) {
      this.#fields.setAttribute("position", position);
    }
  }

  #build(): void {
    this.#built = true;
    const position = String(this.position);
    this.innerHTML = `
      <scapescene-filmstrip position="${position}"></scapescene-filmstrip>
      <div class="screen editor-screen">
        <div class="error-banner" hidden></div>
        <scapescene-frame-fields position="${position}">
        </scapescene-frame-fields>
      </div>
      <scapescene-footer-bar secondary-label="${t("saveDraft")}">
      </scapescene-footer-bar>
    `;
    this.#strip = this.querySelector("scapescene-filmstrip") as HTMLElement;
    this.#fields = this.querySelector(
      "scapescene-frame-fields",
    ) as FieldsElement;
    this.#footer = this.querySelector(
      "scapescene-footer-bar",
    ) as HTMLElement;
    this.#banner = this.querySelector(".error-banner") as HTMLElement;

    this.addEventListener("frame-change", (event) => {
      const detail = (event as CustomEvent<{ position: number }>).detail;
      void this.#fields.flush?.();
      router.navigate(`/frame/${detail.position}`);
    });
    this.addEventListener("film-open", () => {
      void this.#fields.flush?.();
      router.navigate("/film");
    });
    this.addEventListener("setup-open", () => {
      void this.#fields.flush?.();
      router.navigate("/group");
    });
    this.addEventListener("draft-state", (event) => {
      const detail = (
        event as CustomEvent<{
          dirty: boolean;
          saving: boolean;
          error: string;
        }>
      ).detail;
      const note = detail.error
        ? `${t("notSavedPrefix")} ${detail.error}`
        : detail.saving
          ? t("saving")
          : detail.dirty
            ? t("unsavedChanges")
            : t("saved");
      this.#footer.setAttribute("note", note);
    });
    this.addEventListener("footer-secondary", () => {
      void this.#fields.flush?.();
    });
    this.addEventListener("footer-primary", () => {
      void this.#render(false);
    });
    this.addEventListener("footer-extra", () => {
      void this.#render(true);
    });
  }

  /** Run the render step the pressed button stands for: the primary
   * button creates the first frame (which chains the video) until one
   * exists and (re)makes the video after that; the extra button always
   * re-creates the first frame. */
  async #render(newPicture: boolean): Promise<void> {
    if (this.#rendering) return;
    this.#rendering = true;
    this.#banner.hidden = true;
    try {
      await this.#fields.flush?.();
      const frame = store.frame(this.position);
      if (newPicture || !frame?.stillUrl) {
        await store.renderStill(this.position);
      } else {
        await store.renderClip(this.position);
      }
    } catch (error) {
      this.#banner.textContent =
        error instanceof ApiError
          ? error.detail
          : t("renderFailed");
      this.#banner.hidden = false;
    } finally {
      this.#rendering = false;
      this.#syncFooter();
    }
  }

  /** Reflect the frame's pipeline state onto the footer buttons.
   *
   * Caps only bite on a RE-generation (the scene already has its
   * video): once per scene (frameRenderCap) and once for the group
   * (generationCap). A scene without a video can always be rendered. */
  #syncFooter(): void {
    const position = this.position;
    const frame = store.frame(position);
    const group = store.group;
    const status = frame?.videoStatus ?? "idle";
    const busy =
      this.#rendering ||
      status === "making_still" ||
      status === "making_clip";

    const hasStill = Boolean(frame?.stillUrl);
    const hasVideo = Boolean(frame?.videoUrl);
    const sceneLeft =
      group && frame ? group.frameRenderCap - frame.rendersUsed : 0;
    const groupLeft = group
      ? group.generationCap - group.generationsUsed
      : 0;
    // A redo of anything on a scene that has its video spends one of
    // each; the first video is always free.
    const regenBlocked = hasVideo && (sceneLeft < 1 || groupLeft < 1);

    // The primary button walks the pipeline: first frame (+ video)
    // first, then the video again. The extra button re-makes the first
    // frame — and so the video.
    let label = hasStill
      ? hasVideo
        ? t("redoClip")
        : t("makeClip")
      : t("makePicture");
    let extraLabel = hasStill ? t("newPicture") : "";
    let hint = hasStill ? "" : t("makePictureHint");
    let disabled = false;
    let extraDisabled = regenBlocked;

    if (busy) {
      label = t("rendering");
      hint =
        status === "making_clip" ? t("makingClip") : t("makingPicture");
    } else if (group && !group.videoEnabled) {
      disabled = true;
      extraDisabled = true;
      hint = t("videoNotEnabled");
    } else if (!hasStill && position > 1) {
      const previous = store.frame(position - 1);
      if (previous && !previous.stillUrl) {
        disabled = true;
        hint = tn("makePreviousFirst", position - 1);
      }
    }
    if (!disabled && !busy && hasVideo) {
      if (sceneLeft < 1) {
        disabled = true;
        hint = tn("frameCapReached", group?.frameRenderCap ?? 0);
      } else if (groupLeft < 1) {
        disabled = true;
        hint = t("noGenerationsLeft");
      } else {
        hint = tn("regenHint", Math.min(sceneLeft, groupLeft));
      }
    }
    if (!busy && status === "error" && frame?.videoError) {
      hint = frame.videoError.slice(0, 140);
    }

    this.#footer.setAttribute("primary-label", label);
    this.#footer.setAttribute("primary-busy", busy ? "true" : "false");
    this.#footer.setAttribute(
      "primary-disabled",
      disabled ? "true" : "false",
    );
    if (extraLabel) {
      this.#footer.setAttribute("extra-label", extraLabel);
      this.#footer.setAttribute(
        "extra-disabled",
        extraDisabled ? "true" : "false",
      );
    } else {
      this.#footer.removeAttribute("extra-label");
    }
    if (hint) {
      this.#footer.setAttribute("primary-hint", hint);
    } else {
      this.#footer.removeAttribute("primary-hint");
    }
  }
}

if (!customElements.get("scapescene-editor-screen")) {
  customElements.define("scapescene-editor-screen", WorkshopEditorScreen);
}
